import { Injectable } from '@angular/core';
import { Query } from '@datorama/akita';
import { AkitaFirebaseState, AkitaFirebaseStore } from './firebase.store';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { AuthTokenModel } from '@app/shared/models/api/auth/auth-token.model';
import { UserModel } from '@app/shared/models/api/user.model';

@Injectable({ providedIn: 'root' })
export class AkitaFirebaseQuery extends Query<AkitaFirebaseState> {
  constructor(protected store: AkitaFirebaseStore) {
    super(store);
  }

  public get token(): string | null {
    return this.getValue().token;
  }

  public get accessToken(): string | null {
    return this.getValue().token || null;
  }

  public get user(): UserModel | null {
    return this.getValue().user || null;
  }

  public get isLoggedIn(): boolean {
    const firebaseUser = this.getValue().firebaseUser;
    const user = this.getValue().user;
    return !!(firebaseUser && firebaseUser.accessToken && user && user.id);
  }

  public get sessionId(): string {
    return `${this.getValue()?.sessionId || ''}`;
  }

  public getfirebaseAuthToken(): AuthTokenModel | null {
    return {
      accessToken: this.getValue().firebaseUser.accessToken,
      expiresIn: this.getValue().firebaseUser.expiresIn,
      expiresAt: this.getValue().firebaseUser.expiresAt,
    };
  }

  public selectIsLoggedIn(): Observable<boolean> {
    return this.select().pipe(
      map(() => this.isLoggedIn),
      distinctUntilChanged()
    );
  }

  public selectSessionId(): Observable<string> {
    return this.select().pipe(
      map((state: AkitaFirebaseState) => state.sessionId || ''),
      distinctUntilChanged()
    );
  }

  public selectUser(): Observable<UserModel | null> {
    return this.select().pipe(
      map((state: AkitaFirebaseState) => state.user || null),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );
  }

  public selectUserID(): Observable<string> {
    return this.select().pipe(
      map((state: AkitaFirebaseState) => {
        const user = state.user;
        if (user && user.id) {
          return user.id;
        }
        return '';
      }),
      distinctUntilChanged()
    );
  }

  public selectFirebaseUser(): Observable<any | null> {
    return this.select().pipe(
      map((state: AkitaFirebaseState) => state.firebaseUser || null),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );
  }

  public selectFirebaseToken(): Observable<string | null> {
    return this.select().pipe(
      map((state: AkitaFirebaseState) => state.token || null),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );
  }
}
